import React, { useState, useEffect } from 'react';
import { 
  Leaf, 
  Droplets, 
  Zap, 
  Trash2, 
  TreePine, 
  Users, 
  Target,
  Save,
  Loader2,
  Plus,
  Edit3,
  X,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { supabase, isSupabaseConfigured, testConnection } from '../../lib/supabase';

interface ImpactType {
  id: string;
  name: string;
  label: string;
  unit: string;
  icon: string;
  color: string;
  bg_color: string;
  value: number;
  active: boolean;
  order_index: number;
}

interface ImpactStats {
  id: string;
  co2_saved: number;
  waste_reduced: number;
  trees_planted: number;
  water_saved: number;
  energy_saved: number;
  total_users: number;
  total_missions_completed: number;
  updated_at: string;
}

const defaultImpactTypes: ImpactType[] = [
  {
    id: 'co2_saved',
    name: 'co2_saved',
    label: 'CO₂ Saved',
    unit: 'kg',
    icon: 'Leaf',
    color: 'text-green-600',
    bg_color: 'bg-green-100',
    value: 0,
    active: true,
    order_index: 1
  },
  {
    id: 'waste_reduced',
    name: 'waste_reduced',
    label: 'Waste Reduced',
    unit: 'kg',
    icon: 'Trash2',
    color: 'text-orange-600',
    bg_color: 'bg-orange-100',
    value: 0,
    active: true,
    order_index: 2
  },
  {
    id: 'trees_planted',
    name: 'trees_planted',
    label: 'Trees Planted',
    unit: '',
    icon: 'TreePine',
    color: 'text-emerald-600',
    bg_color: 'bg-emerald-100',
    value: 0,
    active: true,
    order_index: 3
  },
  {
    id: 'water_saved',
    name: 'water_saved',
    label: 'Water Saved',
    unit: 'L',
    icon: 'Droplets',
    color: 'text-blue-600',
    bg_color: 'bg-blue-100',
    value: 0,
    active: true,
    order_index: 4
  },
  {
    id: 'energy_saved',
    name: 'energy_saved',
    label: 'Energy Saved',
    unit: 'kWh',
    icon: 'Zap',
    color: 'text-yellow-600',
    bg_color: 'bg-yellow-100',
    value: 0,
    active: true,
    order_index: 5
  },
  {
    id: 'total_users',
    name: 'total_users',
    label: 'Total Users',
    unit: '',
    icon: 'Users',
    color: 'text-purple-600',
    bg_color: 'bg-purple-100',
    value: 0,
    active: true,
    order_index: 6
  },
  {
    id: 'total_missions_completed',
    name: 'total_missions_completed',
    label: 'Missions Completed',
    unit: '',
    icon: 'Target',
    color: 'text-indigo-600',
    bg_color: 'bg-indigo-100',
    value: 0,
    active: true,
    order_index: 7
  }
];

export default function AdminImpact() {
  const [stats, setStats] = useState<ImpactStats | null>(null);
  const [impactTypes, setImpactTypes] = useState<ImpactType[]>(defaultImpactTypes);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingType, setEditingType] = useState<ImpactType | null>(null);
  const [isCreatingType, setIsCreatingType] = useState(false);
  const [editForm, setEditForm] = useState<Partial<ImpactStats>>({});
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const emptyImpactType: Omit<ImpactType, 'id' | 'value'> = {
    name: '',
    label: '',
    unit: '',
    icon: 'Target',
    color: 'text-gray-600',
    bg_color: 'bg-gray-100',
    active: true,
    order_index: impactTypes.length + 1
  };

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    console.log('Initializing admin impact data...');
    setLoading(true);
    setConnectionStatus('checking');
    
    // Test database connection
    const isConnected = await testConnection();
    setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    
    await fetchImpactTypes();
    await fetchStats();
  };

  const fetchImpactTypes = async () => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Using default impact types - Supabase not configured');
        setImpactTypes(defaultImpactTypes);
        return;
      }

      console.log('Fetching impact types from database...');

      const { data, error } = await supabase
        .from('impact_types')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) {
        console.error('Error fetching impact types:', error);
        setImpactTypes(defaultImpactTypes);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No impact types found, using defaults');
        setImpactTypes(defaultImpactTypes);
        return;
      }

      // Add value property (will be filled when stats are fetched)
      const typesWithValues = data.map(type => ({
        ...type,
        value: 0
      }));

      setImpactTypes(typesWithValues);
    } catch (error) {
      console.error('Error fetching impact types:', error);
      setImpactTypes(defaultImpactTypes);
    }
  };

  const fetchStats = async () => {
    try {
      console.log('Fetching impact stats...');
      
      if (!isSupabaseConfigured) {
        console.log('Using mock data - Supabase not configured');
        const mockStats = {
          id: 'mock-1',
          co2_saved: 12847.5,
          waste_reduced: 8924.2,
          trees_planted: 1247,
          water_saved: 445720,
          energy_saved: 9847.3,
          total_users: 50000,
          total_missions_completed: 2300000,
          updated_at: new Date().toISOString()
        };
        setStats(mockStats);
        setEditForm(mockStats);
        
        // Update impact types with values
        setImpactTypes(prev => prev.map(type => ({
          ...type,
          value: mockStats[type.name as keyof ImpactStats] as number || 0
        })));
        
        setMessage({ type: 'success', text: 'Using mock data (Supabase not configured)' });
        setLoading(false);
        return;
      }

      console.log('Fetching impact stats from database...');

      // First, try to get existing stats
      const { data, error } = await supabase
        .from('impact_stats')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching impact stats:', error);
        
        // If error is because table doesn't exist or no data, try to create initial record
        if (error.code === 'PGRST116' || error.message.includes('relation') || !data || data.length === 0) {
          console.log('No impact stats found or table issue, creating initial record...');
          
          const initialStats = {
            co2_saved: 12847.5,
            waste_reduced: 8924.2,
            trees_planted: 1247,
            water_saved: 445720,
            energy_saved: 9847.3,
            total_users: 50000,
            total_missions_completed: 2300000
          };

          const { data: newData, error: insertError } = await supabase
            .from('impact_stats')
            .insert(initialStats)
            .select()
            .single();

          if (insertError) {
            console.error('Error creating initial impact stats:', insertError);
            setMessage({ type: 'error', text: `Database error: ${insertError.message}` });
            return;
          }

          const statsData = newData;
          setStats(statsData);
          setEditForm(statsData);
          
          // Update impact types with values
          setImpactTypes(prev => prev.map(type => ({
            ...type,
            value: statsData[type.name as keyof ImpactStats] as number || 0
          })));

          setMessage({ type: 'success', text: 'Initial data created successfully' });
          return;
        } else {
          setMessage({ type: 'error', text: `Failed to fetch data: ${error.message}` });
          return;
        }
      }

      if (!data || data.length === 0) {
        console.log('No data returned, creating initial record...');
        const initialStats = {
          co2_saved: 12847.5,
          waste_reduced: 8924.2,
          trees_planted: 1247,
          water_saved: 445720,
          energy_saved: 9847.3,
          total_users: 50000,
          total_missions_completed: 2300000
        };

        const { data: newData, error: insertError } = await supabase
          .from('impact_stats')
          .insert(initialStats)
          .select()
          .single();

        if (insertError) {
          console.error('Error creating initial impact stats:', insertError);
          setMessage({ type: 'error', text: `Failed to create initial data: ${insertError.message}` });
          return;
        }

        const statsData = newData;
        setStats(statsData);
        setEditForm(statsData);
        
        // Update impact types with values
        setImpactTypes(prev => prev.map(type => ({
          ...type,
          value: statsData[type.name as keyof ImpactStats] as number || 0
        })));

        setMessage({ type: 'success', text: 'Initial data created successfully' });
        return;
      }

      const statsData = data[0];
      console.log('Fetched impact stats:', statsData);

      setStats(statsData);
      setEditForm(statsData);
      
      // Update impact types with values
      setImpactTypes(prev => prev.map(type => ({
        ...type,
        value: statsData[type.name as keyof ImpactStats] as number || 0
      })));

      setMessage({ type: 'success', text: 'Data loaded successfully' });
    } catch (error) {
      console.error('Error fetching impact stats:', error);
      setMessage({ type: 'error', text: `Unexpected error: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm) {
      setMessage({ type: 'error', text: 'No data to save' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);
      console.log('Saving impact stats...', editForm);

      if (!isSupabaseConfigured) {
        console.log('Mock save impact stats:', editForm);
        const updatedStats = { ...stats, ...editForm } as ImpactStats;
        setStats(updatedStats);
        
        // Update impact types with new values
        setImpactTypes(prev => prev.map(type => ({
          ...type,
          value: editForm[type.name as keyof ImpactStats] as number || type.value
        })));
        
        setSaving(false);
        setMessage({ type: 'success', text: 'Changes saved successfully! (Mock mode)' });
        return;
      }

      // Prepare the update data with proper field mapping and type conversion
      const updateData = {
        co2_saved: Number(editForm.co2_saved) || 0,
        waste_reduced: Number(editForm.waste_reduced) || 0,
        trees_planted: Number(editForm.trees_planted) || 0,
        water_saved: Number(editForm.water_saved) || 0,
        energy_saved: Number(editForm.energy_saved) || 0,
        total_users: Number(editForm.total_users) || 0,
        total_missions_completed: Number(editForm.total_missions_completed) || 0
      };

      console.log('Updating impact stats with:', updateData);

      let result;
      
      if (stats?.id) {
        // Update existing record
        console.log('Updating existing record with ID:', stats.id);
        result = await supabase
          .from('impact_stats')
          .update(updateData)
          .eq('id', stats.id)
          .select()
          .single();
      } else {
        // Create new record
        console.log('Creating new record...');
        result = await supabase
          .from('impact_stats')
          .insert(updateData)
          .select()
          .single();
      }

      const { data, error } = result;

      if (error) {
        console.error('Error saving impact stats:', error);
        setMessage({ type: 'error', text: `Error saving changes: ${error.message}` });
        return;
      }

      console.log('Successfully saved impact stats:', data);
      
      // Update local state with the returned data
      setStats(data);
      setEditForm(data);
      
      // Update impact types with new values
      setImpactTypes(prev => prev.map(type => ({
        ...type,
        value: data[type.name as keyof ImpactStats] as number || type.value
      })));

      setMessage({ type: 'success', text: 'Changes saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error updating impact stats:', error);
      setMessage({ type: 'error', text: `Error saving changes: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveImpactType = async (impactType: ImpactType | Omit<ImpactType, 'id' | 'value'>) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock save impact type:', impactType);
        if ('id' in impactType) {
          // Update existing
          setImpactTypes(prev => prev.map(type => 
            type.id === impactType.id ? { ...impactType, value: type.value } : type
          ));
        } else {
          // Create new
          const newType: ImpactType = {
            ...impactType,
            id: `custom_${Date.now()}`,
            value: 0
          };
          setImpactTypes(prev => [...prev, newType]);
        }
        
        setEditingType(null);
        setIsCreatingType(false);
        return;
      }

      // Filter out the 'value' property before sending to database
      const { value, ...typeDataWithoutValue } = 'id' in impactType ? impactType : {
        ...impactType,
        id: undefined, // Let database generate ID for new types
        value: undefined // Remove value property
      };

      const typeData = typeDataWithoutValue;

      const { error } = await supabase
        .from('impact_types')
        .upsert(typeData, { onConflict: 'name' });

      if (error) {
        console.error('Error saving impact type:', error);
        setMessage({ type: 'error', text: `Error saving impact type: ${error.message}` });
        return;
      }

      // Refresh impact types
      await fetchImpactTypes();
      
      setEditingType(null);
      setIsCreatingType(false);
      setMessage({ type: 'success', text: 'Impact type saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving impact type:', error);
      setMessage({ type: 'error', text: `Error saving impact type: ${(error as Error).message}` });
    }
  };

  const handleDeleteImpactType = async (typeId: string) => {
    if (!confirm('Are you sure you want to delete this impact type?')) return;
    
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock delete impact type:', typeId);
        setImpactTypes(prev => prev.filter(type => type.id !== typeId));
        return;
      }

      const { error } = await supabase
        .from('impact_types')
        .delete()
        .eq('id', typeId);

      if (error) {
        console.error('Error deleting impact type:', error);
        setMessage({ type: 'error', text: `Error deleting impact type: ${error.message}` });
        return;
      }

      // Refresh impact types
      await fetchImpactTypes();
      setMessage({ type: 'success', text: 'Impact type deleted successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error deleting impact type:', error);
      setMessage({ type: 'error', text: `Error deleting impact type: ${(error as Error).message}` });
    }
  };

  const handleInputChange = (fieldName: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    
    // Update edit form
    setEditForm(prev => ({
      ...prev,
      [fieldName]: numericValue
    }));
    
    // Update impact types display immediately for better UX
    setImpactTypes(prev => prev.map(type => 
      type.name === fieldName ? { ...type, value: numericValue } : type
    ));
  };

  const formatValue = (value: number, unit: string) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M ${unit}`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K ${unit}`;
    }
    return `${value.toLocaleString()} ${unit}`;
  };

  const activeImpactTypes = impactTypes.filter(type => type.active).sort((a, b) => a.order_index - b.order_index);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading impact statistics...</p>
          <p className="text-sm text-gray-500 mt-2">
            {connectionStatus === 'checking' ? 'Checking database connection...' : 
             connectionStatus === 'connected' ? 'Connected to database' : 
             'Using mock data'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Global Impact Statistics</h2>
          <p className="text-gray-600 mt-2">Manage and update global environmental impact metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsCreatingType(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Impact Type</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className={`p-4 rounded-lg border ${
        connectionStatus === 'connected' 
          ? 'bg-green-50 border-green-200' 
          : connectionStatus === 'disconnected'
          ? 'bg-yellow-50 border-yellow-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center space-x-2">
          {connectionStatus === 'connected' && <CheckCircle className="h-5 w-5 text-green-600" />}
          {connectionStatus === 'disconnected' && <AlertCircle className="h-5 w-5 text-yellow-600" />}
          {connectionStatus === 'checking' && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
          <span className={`font-medium ${
            connectionStatus === 'connected' 
              ? 'text-green-800' 
              : connectionStatus === 'disconnected'
              ? 'text-yellow-800'
              : 'text-blue-800'
          }`}>
            Database Status: {connectionStatus === 'connected' ? 'Connected' : connectionStatus === 'disconnected' ? 'Using Mock Data' : 'Checking...'}
          </span>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Debug Info */}
      {stats && (
        <div className="bg-gray-100 rounded-lg p-4 text-sm">
          <p><strong>Current Record ID:</strong> {stats.id}</p>
          <p><strong>Supabase Configured:</strong> {isSupabaseConfigured ? 'Yes' : 'No'}</p>
          <p><strong>Last Updated:</strong> {stats.updated_at ? new Date(stats.updated_at).toLocaleString() : 'N/A'}</p>
        </div>
      )}

      {/* Impact Type Form Modal */}
      {(editingType || isCreatingType) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingType ? 'Edit Impact Type' : 'Create New Impact Type'}
              </h3>
              <button
                onClick={() => {
                  setEditingType(null);
                  setIsCreatingType(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ImpactTypeForm
              impactType={editingType || emptyImpactType}
              onSave={handleSaveImpactType}
            />
          </div>
        </div>
      )}

      {/* Impact Types Management */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Impact Types Configuration</h3>
          <p className="text-sm text-gray-600">{impactTypes.length} types configured</p>
        </div>
        
        <div className="space-y-3">
          {impactTypes.map((type) => {
            const IconComponent = (LucideIcons as any)[type.icon] || Target;
            
            return (
              <div key={type.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${type.bg_color}`}>
                    <IconComponent className={`h-5 w-5 ${type.color}`} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{type.label}</h4>
                    <p className="text-sm text-gray-600">
                      Field: {type.name} • Unit: {type.unit || 'none'} • Order: {type.order_index}
                    </p>
                  </div>
                  {!type.active && (
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                      Inactive
                    </span>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingType(type)}
                    className="p-2 text-gray-600 hover:text-blue-600 rounded-lg transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  {!defaultImpactTypes.some(dt => dt.id === type.id) && (
                    <button
                      onClick={() => handleDeleteImpactType(type.id)}
                      className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Impact Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {activeImpactTypes.map((type) => {
          const IconComponent = (LucideIcons as any)[type.icon] || Target;
          
          return (
            <div key={type.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow">
              <div className={`inline-flex p-3 rounded-lg ${type.bg_color} mb-4`}>
                <IconComponent className={`h-6 w-6 ${type.color}`} />
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">{type.label}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatValue(type.value, type.unit)}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Edit Value
                </label>
                <input
                  type="number"
                  value={editForm[type.name as keyof ImpactStats] || ''}
                  onChange={(e) => handleInputChange(type.name, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Enter value"
                  step="any"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Impact Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600">
              {stats ? formatValue(stats.co2_saved + stats.energy_saved, '') : '0'}
            </p>
            <p className="text-sm text-gray-600">Total Environmental Units Saved</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600">
              {stats ? stats.total_users.toLocaleString() : '0'}
            </p>
            <p className="text-sm text-gray-600">Active Eco Warriors</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-600">
              {stats ? (stats.total_missions_completed / Math.max(stats.total_users, 1)).toFixed(1) : '0'}
            </p>
            <p className="text-sm text-gray-600">Avg Missions per User</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Impact Type Form Component
function ImpactTypeForm({ 
  impactType, 
  onSave 
}: { 
  impactType: ImpactType | Omit<ImpactType, 'id' | 'value'>;
  onSave: (impactType: ImpactType | Omit<ImpactType, 'id' | 'value'>) => void;
}) {
  const [formData, setFormData] = useState(impactType);

  const iconOptions = [
    'Target', 'Leaf', 'Droplets', 'Zap', 'Trash2', 'TreePine', 'Users', 
    'Heart', 'Star', 'Award', 'TrendingUp', 'BarChart3', 'Globe', 'Recycle'
  ];

  const colorOptions = [
    { value: 'text-green-600', bg: 'bg-green-100', label: 'Green' },
    { value: 'text-blue-600', bg: 'bg-blue-100', label: 'Blue' },
    { value: 'text-purple-600', bg: 'bg-purple-100', label: 'Purple' },
    { value: 'text-orange-600', bg: 'bg-orange-100', label: 'Orange' },
    { value: 'text-red-600', bg: 'bg-red-100', label: 'Red' },
    { value: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Yellow' },
    { value: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Indigo' },
    { value: 'text-pink-600', bg: 'bg-pink-100', label: 'Pink' },
    { value: 'text-teal-600', bg: 'bg-teal-100', label: 'Teal' },
    { value: 'text-gray-600', bg: 'bg-gray-100', label: 'Gray' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleColorChange = (colorValue: string) => {
    const colorOption = colorOptions.find(opt => opt.value === colorValue);
    if (colorOption) {
      setFormData({
        ...formData,
        color: colorOption.value,
        bg_color: colorOption.bg
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
          <input
            type="text"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., CO₂ Saved"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Field Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., co2_saved"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
          <input
            type="text"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., kg, L, kWh (leave empty for no unit)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Order Index</label>
          <input
            type="number"
            value={formData.order_index}
            onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            min="1"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
          <select
            value={formData.color}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {colorOptions.map(color => (
              <option key={color.value} value={color.value}>{color.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700">Active</span>
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="submit"
          className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          <Save className="h-4 w-4" />
          <span>Save Impact Type</span>
        </button>
      </div>
    </form>
  );
}